import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ENTITY_TYPES } from '@sales-center/common'
import { toast } from '@sales-center/components'
import { adminRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import {
  selectCurrentUser,
  SettingsAdminPermOps,
} from '@user/store/authorization'
import { useCheckAdminPermissions } from '@user/store/authorization/permissions/useCheckPermissions'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { getSalesTeam, getSalesTeamUsers } from '@user/store/salesTeam/api'
import { useGetOwnerList } from '@user/store/salesTeam/hooks/useGetOwnerList'
import { selectAllUsers, useGetUserListOnMount } from '@user/store/users'
import type { OwnerOption } from '@user/features/Activity/components/ActivityForm/ActivityFormFields.types'
import type { AttachedRecord } from './AttachToPicker'

export type OwnerScopeStatus = 'loading' | 'ready' | 'error'

type CreateEventOwners = {
  salesTeamId: number
  owners: OwnerOption[]
  allowedOwnerIds: Set<string> | null
  status: OwnerScopeStatus
}

type TeamLoad = { teamId: number; status: OwnerScopeStatus }

export function useCreateEventOwners(
  attached: AttachedRecord | null,
): CreateEventOwners {
  const dispatch = useAppDispatch()
  const { userId } = useSelector(selectCurrentUser)
  const [showAdminUsers] = useCheckAdminPermissions(SettingsAdminPermOps)

  const usersStatus = useGetUserListOnMount(true)
  const users = useAppSelector(selectAllUsers)

  const teamId = attached?.salesTeamId ?? 0
  const [teamLoad, setTeamLoad] = useState<TeamLoad>({
    teamId: 0,
    status: 'loading',
  })

  useEffect(() => {
    if (!teamId) return
    setTeamLoad({ teamId, status: 'loading' })
    const usersRequest = dispatch(getSalesTeamUsers(teamId))
    const teamRequest = dispatch(getSalesTeam({ id: teamId }))
    let active = true
    Promise.all([usersRequest, teamRequest]).then(([usersResult, team]) => {
      if (!active) return
      const ok =
        getSalesTeamUsers.fulfilled.match(usersResult) &&
        getSalesTeam.fulfilled.match(team)
      if (!ok) toast.error('Error loading owners for this record')
      setTeamLoad({ teamId, status: ok ? 'ready' : 'error' })
    })
    return () => {
      active = false
      usersRequest.abort()
      teamRequest.abort()
    }
  }, [dispatch, teamId])

  const teamOwners = useGetOwnerList(
    attached?.entityType ?? ENTITY_TYPES.Lead,
    teamId,
    false,
    userId,
  )

  const allOwners = useMemo<OwnerOption[]>(
    () =>
      users
        .filter(
          (u) =>
            u.isActive &&
            (showAdminUsers || u.roleName !== adminRoleName || u.id === userId),
        )
        .map((u) => ({ id: u.id, name: u.fullName ?? '' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users, showAdminUsers, userId],
  )

  return useMemo(() => {
    if (!attached) {
      const status: OwnerScopeStatus =
        usersStatus === 'success'
          ? 'ready'
          : usersStatus === 'fail'
            ? 'error'
            : 'loading'
      return {
        salesTeamId: 0,
        owners: allOwners,
        allowedOwnerIds: status === 'ready' ? toIdSet(allOwners) : null,
        status,
      }
    }
    if (!teamId) {
      return {
        salesTeamId: 0,
        owners: [],
        allowedOwnerIds: new Set<string>(),
        status: 'ready',
      }
    }
    const status: OwnerScopeStatus =
      teamLoad.teamId === teamId ? teamLoad.status : 'loading'
    const ready = status === 'ready'
    return {
      salesTeamId: teamId,
      owners: ready ? (teamOwners as OwnerOption[]) : [],
      allowedOwnerIds: ready
        ? toIdSet(teamOwners.filter((o) => !o.isPosition))
        : null,
      status,
    }
  }, [attached, teamId, allOwners, usersStatus, teamLoad, teamOwners])
}

function toIdSet(owners: { id?: string }[]) {
  return new Set(owners.map((o) => o.id).filter((id): id is string => !!id))
}
